import React, { useEffect, useMemo, useRef, useState } from "react";
import Hero from "@/components/Hero";
import MarketTicker from "@/components/MarketTicker";
import SponsorStrip from "@/components/SponsorStrip";
import BookStrip from "@/components/BookStrip";
import CalculatorForm from "@/components/CalculatorForm";
import ProjectionChart from "@/components/ProjectionChart";
import AssetAllocationDonut from "@/components/AssetAllocationDonut";
import YearByYearTable from "@/components/YearByYearTable";
import EducationSection from "@/components/EducationSection";
import ResultsSummary from "@/components/ResultsSummary";
import EbookTeaserCard from "@/components/EbookTeaserCard";
import { fetchMarketSnapshot, runProjection } from "@/lib/api";
import { toast } from "sonner";
import { Code2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GoalSeekDialog from "@/components/GoalSeekDialog";
import StressTestPanel from "@/components/StressTestPanel";
import SamplePathsViz from "@/components/SamplePathsViz";
import AssetClassDrilldown from "@/components/AssetClassDrilldown";
import EmbedSnippet from "@/components/EmbedSnippet";
import PlanCritique from "@/components/PlanCritique";
import AffiliatesPanel from "@/components/AffiliatesPanel";
import { buildShareUrl, readPlanFromUrl } from "@/lib/shareUrl";
import { EmailResultsCard } from "@/components/EmailResultsCard";
import { exportPlanPDF } from "@/lib/pdfExport";
import { secureGetJSON, secureSet } from "@/lib/secureStorage";
import { errorMessage } from "@/lib/errorMessage";
import useSEO from "@/lib/useSEO";
import { track, EVENTS } from "@/lib/analytics";

const DEFAULT_INPUTS = {
  current_age: 32,
  retirement_age: 65,
  life_expectancy: 100,
  current_savings: 50000,
  monthly_contribution: 1500,
  inflation_rate: 2.5,
  annual_withdrawal: 60000,
  allocation: { stocks: 70, bonds: 25, cash: 5 },
  simulations: 1500,
  withdrawal_strategy: "fixed_real",
  withdrawal_percentage: 4.0,
  guardrails: null,
  income_streams: [],
  tax_config: null,
  healthcare: null,
  real_estate: null,
  glide_path: null,
  roth_conversion: null,
};

const STORAGE_KEY = "centennial_scenarios_v1";

export default function Home() {
  useSEO({
    title: "Centennial — Free 100-Year Retirement Calculator | Monte Carlo + Live Markets",
    description: "Project your portfolio to age 100 using 1,500 Monte Carlo simulations on live market data. Free forever. No signup. Models Social Security, taxes, healthcare, Roth ladders, and historical stress tests for 1929, 1973, 2000, 2008.",
    canonicalPath: "/",
  });
  const [snapshot, setSnapshot] = useState(null);
  const [marketLoading, setMarketLoading] = useState(true);
  const [inputs, setInputs] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_INPUTS;
    const shared = readPlanFromUrl();
    return shared ? { ...DEFAULT_INPUTS, ...shared } : DEFAULT_INPUTS;
  });
  const [sharedLoaded, setSharedLoaded] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!readPlanFromUrl();
  });
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [mode, setMode] = useState("both"); // simple | monte | both
  const [scenarios, setScenarios] = useState([]);
  const [goalOpen, setGoalOpen] = useState(false);
  const dashboardRef = useRef(null);

  // Load market snapshot on mount (single-run effect — intentional empty deps)
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await fetchMarketSnapshot();
        if (mounted) setSnapshot(s);
      } catch (e) {
        console.warn("[Centennial] market snapshot fetch failed:", e);
      } finally {
        if (mounted) setMarketLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved scenarios from localStorage; toast if a shared plan was restored from URL
  useEffect(() => {
    const list = secureGetJSON(STORAGE_KEY, null);
    if (list) setScenarios(list);
    if (sharedLoaded) {
      // Defer to next tick so Sonner has subscribed to its store
      setTimeout(() => toast.success("Loaded shared plan from URL."), 0);
    }
  }, [sharedLoaded]);

  // Auto-run an initial projection on mount once snapshot loaded
  useEffect(() => {
    if (snapshot && !result && !running) {
      handleRun(inputs);
    }
    // eslint-disable-next-line
  }, [snapshot]);

  // Fire homepage_view once per mount for PostHog funnel
  useEffect(() => {
    track(EVENTS.HOME_VIEW, { shared_plan: sharedLoaded });
    // eslint-disable-next-line
  }, []);

  const handleRun = async (override) => {
    const payload = override ?? inputs;
    const sum =
      payload.allocation.stocks + payload.allocation.bonds + payload.allocation.cash;
    if (Math.abs(sum - 100) > 0.5) {
      toast.error(`Allocation totals ${sum}% — must equal 100%.`);
      return;
    }
    if (payload.retirement_age <= payload.current_age) {
      toast.error("Retirement age must be greater than current age.");
      return;
    }
    if (payload.life_expectancy <= payload.retirement_age) {
      toast.error("Life expectancy must be greater than retirement age.");
      return;
    }
    setRunning(true);
    try {
      const res = await runProjection(payload);
      setResult(res);
      track(EVENTS.CALCULATOR_RUN, {
        current_age: payload.current_age,
        retirement_age: payload.retirement_age,
        simulations: payload.simulations,
        success_prob: res?.success_probability ?? null,
      });
    } catch (e) {
      console.error(e);
      toast.error(errorMessage(e, "Projection failed. Try again."));
    } finally {
      setRunning(false);
    }
  };

  const handleSave = () => {
    const name = prompt("Name this scenario:", `Plan @ ${new Date().toLocaleDateString()}`);
    if (!name) return;
    const entry = { id: crypto.randomUUID(), name, inputs, created_at: new Date().toISOString() };
    const next = [entry, ...scenarios].slice(0, 8);
    setScenarios(next);
    secureSet(STORAGE_KEY, next);
    toast.success(`Saved "${name}" to this browser.`);
  };

  const handleLoadScenario = (id) => {
    const found = scenarios.find((s) => s.id === id);
    if (!found) return;
    setInputs(found.inputs);
    toast.message(`Loaded "${found.name}"`);
    setTimeout(() => handleRun(found.inputs), 0);
  };

  const handleDeleteScenario = (id) => {
    const next = scenarios.filter((s) => s.id !== id);
    setScenarios(next);
    secureSet(STORAGE_KEY, next);
  };

  const handleReset = () => {
    setInputs(DEFAULT_INPUTS);
    toast.message("Reset to defaults");
  };

  const handleShare = async () => {
    const url = buildShareUrl(inputs);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste anywhere for a live preview card.");
    } catch {
      window.prompt("Copy this link:", url);
    }
    // Also reflect plan in URL bar (using SPA hash, not the OG-share URL)
    try {
      const hashUrl = new URL(window.location.href);
      const enc = url.split("p=")[1];
      hashUrl.hash = `p=${enc}`;
      hashUrl.search = "";
      window.history.replaceState(null, "", hashUrl.toString());
    } catch (e) {
      console.warn("[Centennial] could not update URL hash:", e);
    }
  };

  const handleExportPDF = async () => {
    if (!result) {
      toast.error("Run a projection first.");
      return;
    }
    toast.message("Generating PDF…");
    try {
      await exportPlanPDF({
        inputs,
        result,
        chartSelector: "[data-testid='projection-chart']",
      });
    } catch (e) {
      console.error(e);
      toast.error("PDF export failed.");
    }
  };

  const jumpToCalc = () => {
    dashboardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const portfolioStats = useMemo(() => {
    if (!result) return null;
    return result.assumptions;
  }, [result]);

  return (
    <div data-testid="home-page">
      {/* Nav */}
      <header
        data-testid="site-nav"
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-white/40 shadow-[0_1px_0_rgba(28,43,35,0.04)]"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="MyCentennialPlan logo" className="h-8 w-auto" data-testid="nav-logo" />
            <span className="font-display text-[#1C2B23] tracking-tight text-lg">
              MyCentennialPlan<span className="text-[#C26747]">.</span>
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[#1C2B23]">
            <a href="#how-it-works" className="hover:text-[#2B5339]">Method</a>
            <a href="#dashboard" className="hover:text-[#2B5339]">Calculator</a>
            <Link data-testid="nav-compare" to="/compare" className="hover:text-[#2B5339]">Compare</Link>
            <Link data-testid="nav-learn" to="/learn" className="hover:text-[#2B5339]">Learn</Link>
            <Link data-testid="nav-books" to="/books" className="hover:text-[#2B5339]">Books</Link>
            <Link data-testid="nav-medications" to="/medication-safety" className="hover:text-[#2B5339]">Meds</Link>
            <Link data-testid="nav-featured" to="/featured" className="hover:text-[#2B5339]">Featured</Link>
            <Link data-testid="nav-pricing" to="/pricing" className="hover:text-[#2B5339]">Pricing</Link>
            <a
              data-testid="nav-cta"
              href="#dashboard"
              onClick={jumpToCalc}
              className="bg-[#2B5339] text-white hover:bg-[#1E3B28] rounded-full px-4 py-2 text-sm"
            >
              Start free
            </a>
          </nav>
        </div>
      </header>

      <Hero onJump={jumpToCalc} />

      <MarketTicker snapshot={snapshot} loading={marketLoading} />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SponsorStrip />
      </div>

      {/* Dashboard */}
      <section
        id="dashboard"
        ref={dashboardRef}
        data-testid="dashboard-section"
        className="py-20 lg:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#5C6B62] font-semibold">
                the calculator
              </div>
              <h2 className="font-display mt-2 text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-[#1C2B23]">
                Project your portfolio to age 100.
              </h2>
            </div>
            <Tabs value={mode} onValueChange={setMode} className="w-auto">
              <TabsList data-testid="mode-tabs" className="bg-[#F0EFEA] border border-[#E5E2DC]">
                <TabsTrigger data-testid="tab-both" value="both">Both</TabsTrigger>
                <TabsTrigger data-testid="tab-simple" value="simple">Simple</TabsTrigger>
                <TabsTrigger data-testid="tab-monte" value="monte">Monte Carlo</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <CalculatorForm
                inputs={inputs}
                setInputs={setInputs}
                onRun={() => handleRun()}
                onSave={handleSave}
                onReset={handleReset}
                onGoalSeek={() => setGoalOpen(true)}
                onShare={handleShare}
                onExportPDF={handleExportPDF}
                loading={running}
              />
            </div>

            <div className="col-span-12 lg:col-span-8 space-y-6">
              <ResultsSummary result={result} />

              {/* Email-my-results subscriber capture */}
              {result && <EmailResultsCard inputs={inputs} result={result} />}

              {/* Waitlist teaser — only after a projection has run */}
              {result && <EbookTeaserCard />}

              {/* Subtle "Related reading" strip only after a projection has run */}
              {result && (
                <BookStrip category="retirement" title="Books that go deeper on this" limit={3} />
              )}

              <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8">
                <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                      portfolio over time
                    </div>
                    <div className="font-display text-lg text-[#1C2B23] mt-1">
                      Real (inflation-adjusted) balance — today's dollars
                    </div>
                  </div>
                  {portfolioStats && (
                    <div className="text-xs text-[#5C6B62] font-mono-num">
                      μ {(portfolioStats.portfolio_mean_nominal * 100).toFixed(2)}% ·
                      σ {(portfolioStats.portfolio_volatility * 100).toFixed(2)}% ·
                      π {(portfolioStats.inflation * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
                <ProjectionChart
                  result={result}
                  mode={mode}
                  retirementAge={result?.inputs?.retirement_age}
                />
                <div className="mt-4 flex items-center gap-6 text-xs text-[#5C6B62] flex-wrap">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-[3px] bg-[#2B5339] rounded-full" /> Monte Carlo median (p50)
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm" style={{ background: "rgba(114,138,124,0.35)" }} /> p10–p90 band
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-[3px] bg-[#C26747] rounded-full" style={{ borderTop: "1px dashed #C26747" }} /> Simple projection
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
                  <AssetAllocationDonut allocation={inputs.allocation} />
                </div>
                <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                    Saved scenarios
                  </div>
                  <div className="font-display text-lg text-[#1C2B23] mt-1">This browser only</div>
                  <ul data-testid="saved-scenarios" className="mt-4 space-y-2 max-h-40 overflow-auto pr-1">
                    {scenarios.length === 0 && (
                      <li className="text-sm text-[#5C6B62]">
                        No scenarios saved yet. Click <span className="text-[#1C2B23] font-medium">Save</span> to keep one.
                      </li>
                    )}
                    {scenarios.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between border border-[#E5E2DC] rounded-lg px-3 py-2 text-sm"
                      >
                        <button
                          data-testid={`scenario-load-${s.id}`}
                          onClick={() => handleLoadScenario(s.id)}
                          className="text-left flex-1 text-[#1C2B23] hover:text-[#2B5339] truncate mr-2"
                          title={s.name}
                        >
                          {s.name}
                        </button>
                        <button
                          data-testid={`scenario-delete-${s.id}`}
                          onClick={() => handleDeleteScenario(s.id)}
                          className="text-xs text-[#5C6B62] hover:text-[#C26747]"
                        >
                          remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <YearByYearTable result={result} />

              {result && <AssetClassDrilldown assumptions={result.assumptions} />}

              <SamplePathsViz inputs={inputs} retirementAge={inputs.retirement_age} />

              <StressTestPanel inputs={inputs} />

              <PlanCritique inputs={inputs} result={result} />
            </div>
          </div>
        </div>
      </section>

      <AffiliatesPanel />

      <EducationSection />

      <EmbedSnippet />

      {/* Footer */}
      <footer data-testid="site-footer" className="py-14 border-t border-[#E5E2DC] bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="MyCentennialPlan logo" className="h-7 w-auto" data-testid="footer-logo" />
              <span className="font-display text-[#1C2B23]">MyCentennialPlan<span className="text-[#C26747]">.</span></span>
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[#B08A3C] font-semibold">Plan today. Enjoy tomorrow.</div>
          </div>
          <div className="text-xs text-[#5C6B62] max-w-xl">
            Not financial advice. Live market data via Yahoo Finance. Projections use real
            (inflation-adjusted) dollars. Past returns are not a guarantee — that's why we run
            the simulation thousands of times.
          </div>
          <div className="flex items-center gap-4">
            <Link
              data-testid="footer-embed-link"
              to="/embed"
              target="_blank"
              className="text-xs text-[#5C6B62] hover:text-[#2B5339] inline-flex items-center gap-1"
            >
              <Code2 size={12} /> embed widget
            </Link>
          </div>
          <div className="w-full flex flex-wrap gap-x-5 gap-y-2 pt-4 border-t border-[#E5E2DC]">
            {[
              ["About", "/about"],
              ["Methodology", "/methodology"],
              ["Contact", "/contact"],
              ["Privacy", "/privacy"],
              ["Terms", "/terms"],
              ["Disclaimer", "/disclaimer"],
              ["Guides", "/learn"],
              ["Books", "/books"],
              ["Medication Safety", "/medication-safety"],
            ].map(([label, href]) => (
              <Link
                key={href}
                data-testid={`footer-link-${href.replace(/\//g, "") || "home"}`}
                to={href}
                className="text-xs text-[#5C6B62] hover:text-[#2B5339]"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </footer>

      <GoalSeekDialog
        open={goalOpen}
        onOpenChange={setGoalOpen}
        inputs={inputs}
        onApply={(patched) => {
          setInputs(patched);
          setTimeout(() => handleRun(patched), 50);
        }}
      />
    </div>
  );
}
